import { Location } from '@angular/common';
import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../app.service';
import { FeatureToggle } from '@Enums';
import { FeatureManagerService } from './global/feature-manager/feature-manager.service';


export enum RoutesEnum {
    audit = './audit',
    dashboard = '/dashboard',
    myDashboard = '/mydashboard',
    myCases = '/mycases',
    caseMananger = '/casemanager',
    caseManangerView = '/casemanager/view',
    caseManangerEdit = '/casemanager/edit',
    clients = '/clients',
    client = '/client/:clientCode',
    clientDetails = '/client/:clientCode/details',
    accountCreation = '/client/:clientCode/account/new',
    accountDetails = '/client/:clientCode/account/:accountCode/:versionNumber',
    accountDetailsApplication = '/client/:clientCode/account/:accountCode/:versionNumber/configuration/application',
    interview = '/client/:clientCode/account/:accountCode/:versionNumber/interview',
    interviewRestart = '/client/:clientCode/account/:accountCode/:versionNumber/interview/:caseId',
    caseSummary = '/client/:clientCode/account/:accountCode/:versionNumber/:caseId/summary',
    caseSummaryV2 = '/client/:clientCode/account/:accountCode/:versionNumber/:caseId/summaryV2',
    roles = '/roles',
    teams = '/teams',
    teamOverview = '/team/:teamId/overview',
    users = '/users',
    accounts = '/accounts',
    reports = '/reports',
    callManager = '/callmanager',
    endOfCall = '/navigation/endofcall/:id/:tenant',
    system = '/system',
    myAccount = '/myaccount',
    login = '/login',
    loginV2 = '/authorize',
    default = '|default|',
    endOfInterview = '/end-of-application',
    purlInterviewStart = '/purl/client/:clientCode/account/:accountCode/:versionNumber/interview/:caseId',
    purlV2InterviewStart = '/purl/client/:clientCode/account/:accountCode/:versionNumber/interview/:caseId',
    purlV2 = '/purlv2/:id',
    purl = '/purl/:id',
    userManagement = '/manage-users',
    home = '',
    notFound = '/404',
    invalidAuth = '/invalid/:error'
}

@Injectable()
export class RoutingService {

    constructor(
        private readonly appService: AppService,
        private readonly location: Location,
        private readonly router: Router,
        private readonly zone: NgZone,
        private readonly featureManagerService: FeatureManagerService) { }
    public navigateToDefault() {
        const defaultRoute = this.getDefaultRoute();
        this.router.navigate([defaultRoute]);
    }

    public navigateToIntendedOrDefault(defaultRoute?: RoutesEnum) {
        const enableDeepLinking = this.featureManagerService.getByName(FeatureToggle.GlobalRoutingDeepLinking).enabled;
        
        if (defaultRoute === undefined) {
            defaultRoute = this.getDefaultRoute();
        }

        if (!enableDeepLinking) {
            this.navigateToRoute(defaultRoute);
            return;
        }

        const redirectTo = localStorage.getItem('uwpipeline.savedRedirectTo');        
        
        // Prevent redirecting to login/authorization pages or empty URLs
        if (redirectTo && 
            redirectTo !== '/authorize' && 
            redirectTo !== '/login' && 
            redirectTo !== '/' &&
            !redirectTo.includes('/authorize') &&
            !redirectTo.includes('/login')) {
            
            // Clear the stored redirect URL
            localStorage.removeItem('uwpipeline.savedRedirectTo');
            
            // Navigate to the originally intended URL
            console.log(`Redirecting to originally intended URL: ${redirectTo}`);
            this.navigateToUrl(redirectTo);
        } else {
            // No redirect URL or it's a login page, go to default
            this.navigateToRoute(defaultRoute);
        }
    }

    public navigateToNotFound() {
        this.router.navigate([RoutesEnum.notFound], { skipLocationChange: true });
    }
    public getDefaultRoute(): RoutesEnum {
        return this.appService.checkACL('Dashboard', 'V') ?
            RoutesEnum.dashboard
            : RoutesEnum.myCases;
    }

    public navigateToRoute(route: RoutesEnum, params?: object, queryParams?: object) {
        console.log(`navigate to route: ${route}`, queryParams);
        // TODO: Remove this code, once the queryParams are removed from Team API calls
        const navExtras: any = { queryParams };
        if (queryParams && Object.keys(queryParams).length > 0) {
            navExtras.queryParamsHandling = 'merge';
        }

        this.router.navigateByUrl(
            this.router.createUrlTree([this.createUrl(route, params)], navExtras)
        );
    }

    public navigateToRouteInNewTab(route: RoutesEnum, params?: object, queryParams?: object) {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([this.createUrl(route, params)], {
                queryParams,
                queryParamsHandling: 'merge'
            }));

        window.open(url, '_blank');
    }

    public setLocationWithoutRouting(route: RoutesEnum, params?: object) {
        console.log(`setting url to: ${route}`);
        this.location.go(this.createUrl(route, params));
    }

    public navigateToUrl(url: string) {
        return this.zone.run(() => {
            console.log(`navigating straight to url: ${url}`);
            return this.router.navigateByUrl(url);
        });
    }

    public async navigateToAsync(url: string, route?: ActivatedRoute) {
        if (window.location.href.toLowerCase().includes(url.toLowerCase())) {
            // browser refresh
            return true;
        }
        return this.zone.run(() => {
            
            console.log(`navigating to url: ${url}`);
            return this.router.navigate([url], { relativeTo: route });
        });
    }

    public createUrl(route: RoutesEnum, params?: object) {
        let routePath: string = route;
        if (route === RoutesEnum.default) {
            routePath = this.getDefaultRoute();
        } else {
            if (params) {
                for (const key in params) {
                    // e.g.
                    //      routePath = '/:clientId/account/:accountId
                    //      params = {
                    //          clientId: 103,
                    //          accountId: 0008
                    //      }
                    //
                    //      result: /103/account/0008
                    //

                    routePath = routePath.replace(`:${key}`, params[key]);
                }
            }
        }

        return routePath;
    }

    public url() { return this.router.url; }
}
